import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { ListenButton } from '../../common/AudioGuide';
import HelpWithVideoTour from '../../Help/HelpWithVideoTour';

const METRICS = [
  { key: 'mech', label: 'MECH', bg: '#f4a261', color: '#1a1a1a' },
  { key: 'elec', label: 'ELEC', bg: '#e9c46a', color: '#1a1a1a' },
  { key: 'pipe', label: 'PIPE', bg: '#2a6fbb', color: '#fff' },
  { key: 'punch', label: 'Pun. List', bg: '#ffffff', color: '#1a1a1a', border: '#94a3b8' },
  { key: 'tccc', label: 'TCCC', bg: '#9ca3af', color: '#1a1a1a' },
];

const fmtPct = (n) => `${(Number(n) || 0).toFixed(1)}%`;

function ProgressRow({ metric, value }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, lineHeight: 1.15, minHeight: 14 }}>
      <span
        style={{
          width: 52,
          flexShrink: 0,
          padding: '1px 3px',
          background: metric.bg,
          color: metric.color,
          border: metric.border ? `1px solid ${metric.border}` : '1px solid transparent',
          fontWeight: 700,
          textAlign: 'left',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        {metric.label}
      </span>
      <div style={{ flex: 1, height: 10, background: '#f1f5f9', border: '1px solid #cbd5e1', position: 'relative', minWidth: 28 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: '#22c55e' }} />
      </div>
      <span style={{ width: 36, textAlign: 'right', fontVariantNumeric: 'tabular-nums', flexShrink: 0, color: '#0f172a' }}>
        {fmtPct(pct)}
      </span>
    </div>
  );
}

function PressCard({ press }) {
  if (!press) return null;
  return (
    <div
      style={{
        border: '1px solid #334155',
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}
    >
      <div
        style={{
          background: '#e2e8f0',
          borderBottom: '1px solid #334155',
          textAlign: 'center',
          fontWeight: 700,
          fontSize: 10,
          padding: '2px 2px',
          color: '#0f172a',
        }}
      >
        {press.id}
      </div>
      <div style={{ padding: '3px 3px 4px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {METRICS.map((m) => (
          <ProgressRow key={m.key} metric={m} value={press[m.key]} />
        ))}
      </div>
    </div>
  );
}

/**
 * Goodyear / 75998 Press Progress one-pager — Overall vs Last Week gain.
 * Opened from GA Dashboards list when the job has Press-* P6 activities.
 */
const PressProgressOnePager = ({ jobNumber, onBack }) => {
  const { instance, accounts } = useMsal();
  const [data, setData] = useState(null);
  const [mode, setMode] = useState('overall'); // overall | lastWeek
  const [rev, setRev] = useState(''); // P6 proj id — empty = server default (live Current)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [present, setPresent] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const pageRef = useRef(null);

  const load = useCallback(async () => {
    if (!jobNumber) return;
    try {
      setLoading(true);
      setError(null);
      const qs = rev ? `?rev=${encodeURIComponent(rev)}` : '';
      const res = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/ga-dashboards/${jobNumber}/press-progress${qs}`
      );
      const payload = res.data.data;
      setData(payload);
      if (!rev && payload?.selectedRev) setRev(String(payload.selectedRev));
    } catch (e) {
      console.error('Press progress load failed:', e);
      setError(e.response?.data?.error || 'Failed to load press progress');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, jobNumber, rev]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!present) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setPresent(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [present]);

  const exportPng = async () => {
    const node = pageRef.current;
    if (!node) return;
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(node, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      const a = document.createElement('a');
      a.download = `75998-Press-Progress-${mode}-${new Date().toISOString().slice(0, 10)}.png`;
      a.href = canvas.toDataURL('image/png');
      a.click();
    } catch (e) {
      alert('PNG export failed — is html2canvas available?');
      console.error(e);
    }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading press progress from P6…</div>;
  }

  if (error || !data?.available || data?.offline || !data?.overall) {
    return (
      <div style={{ padding: '1rem 1.5rem' }}>
        <button onClick={onBack} style={btnSecondary}>← Back</button>
        <div style={{ marginTop: 16, padding: 16, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, color: '#b91c1c' }}>
          {error || data?.reason || 'Press progress not available for this job'}
        </div>
        {data?.offline && (
          <div style={{ marginTop: 12, fontSize: 13, color: '#64748b', maxWidth: 560 }}>
            The portal code is deployed. This page needs a live connection to Primavera P6 SQL
            (<code>lgl24</code> on port 1433). Check that the P6 SQL Server VM is running and that
            its firewall allows the portal VM (<code>10.0.0.4</code> / public outbound).
          </div>
        )}
        <button onClick={load} style={{ ...btnSecondary, marginTop: 12 }}>Retry</button>
      </div>
    );
  }

  const view = mode === 'lastWeek' ? data.lastWeek : data.overall;
  const shell = {
    padding: present ? 12 : '0.75rem 1rem 1.25rem',
    background: present ? '#0f172a' : 'transparent',
    minHeight: present ? '100vh' : undefined,
    boxSizing: 'border-box',
  };

  return (
    <div style={shell}>
      {!present && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <button onClick={onBack} style={btnSecondary}>← Back</button>
          <h2 style={{ margin: 0, fontSize: 20 }}>Press Progress</h2>
          <span style={{ color: '#64748b', fontSize: 13 }}>
            Job {data.jobNumber}
          </span>
          {(data.revisions || []).length > 0 && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}>
              Rev
              <select
                value={rev || data.selectedRev || ''}
                onChange={(e) => setRev(e.target.value)}
                style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12, maxWidth: 280 }}
              >
                {(data.revisions || []).map((r) => (
                  <option key={r.id} value={r.id}>{r.label}</option>
                ))}
              </select>
            </label>
          )}
          <div style={{ flex: 1 }} />
          <ModeToggle mode={mode} setMode={setMode} />
          <button onClick={() => setShowHelp(true)} title="Help" style={{ ...btnSecondary, padding: '7px 10px' }}>❓</button>
          <button onClick={load} style={btnSecondary}>↻ Refresh</button>
          <button onClick={exportPng} style={btnSecondary}>⬇ PNG</button>
          <button onClick={() => setPresent(true)} style={btnPrimary}>Present</button>
        </div>
      )}

      {!present && data.emptyDated?.length > 0 && (
        <div style={{ marginBottom: 10, padding: '8px 12px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
          P6 has empty dated rev(s) with no activities: {data.emptyDated.map((d) => d.projShortName).join(', ')}.
          {' '}Copy/import the schedule into that project for it to appear as a selectable rev. Using <strong>{data.sources?.overall?.projShortName}</strong> for now.
        </div>
      )}

      {showHelp && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 520, maxWidth: '94vw', overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', background: '#0f766e', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>❓ Press Progress Help</strong>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <ListenButton compact guideId="press-progress-help" title="Press Progress Help" accent="#0f766e" dark />
                <button onClick={() => setShowHelp(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
              </div>
            </div>
            <div style={{ padding: 18, fontSize: 14, lineHeight: 1.55, color: '#334155' }}>
              <HelpWithVideoTour guideId="press-progress-help" title="Press Progress Help" accent="#0f766e">
              <p><strong>Rev</strong> picks which P6 copy to show. Default is live <em>Current</em> (mid-week updates). Dated copies stay in the list as history.</p>
              <p><strong>Overall</strong> = cumulative % for the selected rev.</p>
              <p><strong>Last Week</strong> = gain vs the previous dated submission (floored at 0%).</p>
              <p>MECH / ELEC / PIPE use actual ÷ budget labor units. Pun. List uses P6 physical %. TCCC uses P6 TCCC activities when present, otherwise <strong>Completion Milestone</strong> notebook notes only (e.g. “TCCC signed - J1 to J5”) — punch-list notes are ignored.</p>
              </HelpWithVideoTour>
            </div>
          </div>
        </div>
      )}

      {present && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, color: '#e2e8f0' }}>
          <ModeToggle mode={mode} setMode={setMode} dark />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={exportPng} style={btnSecondary}>⬇ PNG</button>
            <button onClick={() => setPresent(false)} style={btnPrimary}>Exit (Esc)</button>
          </div>
        </div>
      )}

      <div
        ref={pageRef}
        style={{
          background: '#fff',
          border: '2px solid #0f172a',
          padding: '10px 10px 12px',
          boxShadow: present ? '0 8px 32px rgba(0,0,0,0.35)' : 'none',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', letterSpacing: 0.2 }}>
            Goodyear Project Progress Per Press
          </div>
          <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>
            <strong>{view.label}</strong>
            {view.asOf ? <> · based on {view.asOf} update</> : null}
            {mode === 'lastWeek' && view.comparedTo ? <> · vs {view.comparedTo}</> : null}
          </div>
          {mode === 'lastWeek' && view.note && (
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{view.note}</div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(view.grid || []).map((row) => (
            <div key={row.letter}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: '#0f172a',
                  marginBottom: 3,
                  paddingLeft: 2,
                }}
              >
                Row {row.letter}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(10, minmax(0, 1fr))',
                  gap: 4,
                  borderTop: '3px solid #0f172a',
                  paddingTop: 4,
                }}
              >
                {row.presses.map((p) => (
                  <PressCard key={p.id} press={p} />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 10, fontSize: 10, color: '#64748b', display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <span>
            MECH / ELEC / PIPE = Actual ÷ Budget labor units · Pun. List = P6 physical %
            {data.sources?.tccc?.source === 'p6_notebook'
              ? ' · TCCC from P6 Notebook (TCCC signed …)'
              : data.sources?.tccc?.source === 'p6_activity'
                ? ' · TCCC from P6 activities'
                : ' · TCCC from P6'}
          </span>
          <span>Live from P6 · {data.sources?.overall?.projShortName}</span>
        </div>
      </div>
    </div>
  );
};

function ModeToggle({ mode, setMode, dark }) {
  const base = {
    display: 'inline-flex',
    borderRadius: 8,
    overflow: 'hidden',
    border: dark ? '1px solid #475569' : '1px solid #cbd5e1',
  };
  const tab = (id, label) => {
    const active = mode === id;
    return (
      <button
        type="button"
        onClick={() => setMode(id)}
        style={{
          padding: '7px 14px',
          border: 'none',
          cursor: 'pointer',
          fontWeight: 700,
          fontSize: 12,
          background: active ? '#0f766e' : (dark ? '#1e293b' : '#fff'),
          color: active ? '#fff' : (dark ? '#e2e8f0' : '#334155'),
        }}
      >
        {label}
      </button>
    );
  };
  return (
    <div style={base}>
      {tab('overall', 'Overall')}
      {tab('lastWeek', 'Last Week')}
    </div>
  );
}

const btnSecondary = {
  padding: '7px 12px',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  background: '#fff',
  cursor: 'pointer',
  fontSize: 13,
  color: '#334155',
};

const btnPrimary = {
  padding: '7px 14px',
  borderRadius: 8,
  border: 'none',
  background: '#0f766e',
  color: '#fff',
  fontWeight: 700,
  cursor: 'pointer',
  fontSize: 13,
};

export default PressProgressOnePager;
